
import { Component, Prop, Vue } from "vue-property-decorator";
import { UserModule } from "@/store/modules/user";
import moment from "moment";
import {
  calcOverdueDays,
  calcRemainingDays,
  formatDate,
  isCompletedStatus,
  isRemainingWarning,
  parseDay,
  resolveDeliveryDate,
} from "@/views-oms/sales-order-management/order/pi-details/utils";
import {detailQueryDeliveryDate, updatePiDeliveryDate} from "@/api/proforma";

interface DeliveryDateSummary {
  piId: string;
  deliveryDays: number;
  firstOrderDate: string;
  deliveryDate: string;
  pos: Array<PurchaseOrderDeliveryDate>
}

interface PurchaseOrderDeliveryDate {
  code: string;
  supplierAbbr: string;
  purchaser: string;
  deliveryDate: string;
}

@Component({ name: "PiDetailsHeader" })
export default class extends Vue {
  @Prop({ type: String, required: true}) private piId: string;
  @Prop({ type: Object, default: () => ({}) }) private piInfo!: Record<string, any>;
  @Prop({ type: Array, default: () => [] }) private shipmentStatusList!: Array<{
    name: string;
    desc?: string;
  }>;

  private editDeliveryDateDialog = false;
  private detailDeliveryDate: DeliveryDateSummary = {
    piId: "",
    deliveryDays: 0,
    firstOrderDate: "",
    deliveryDate: "",
    pos: [],
  };

  private get customerLabel() {
    const abbr = this.piInfo.customerAbbr;
    if (!abbr) {
      return "—";
    }
    return this.piInfo.pcName ? `[${this.piInfo.pcName}]${abbr}` : abbr;
  }

  private get remarkText() {
    return this.displayValue(this.piInfo.remark);
  }

  private get showCompanyAmount() {
    return (UserModule.accountType || []).some((a) => ["B", "A_B"].includes(a));
  }

  private get showShipmentAmount() {
    return (UserModule.accountType || []).some((a) => ["A", "A_B"].includes(a));
  }

  private get orderAmount() {
    let amount = null as any;
    if (this.showShipmentAmount && this.piInfo.shipmentAmount != null) {
      amount = this.piInfo.shipmentAmount;
    } else if (this.showCompanyAmount && this.piInfo.companyAmount != null) {
      amount = this.piInfo.companyAmount;
    }
    if (amount == null || amount === "") {
      return "—";
    }
    const currency = this.piInfo.currency;
    const amountText = this.displayValue(amount);
    return currency ? `${amountText} ${currency}` : amountText;
  }

  private get deliveryAmountText() {
    const amount = this.piInfo.deliveryAmount;
    if (amount == null || amount === "") {
      return "—";
    }
    return `${this.displayValue(amount)} RMB`;
  }

  private get deliveryDateText() {
    const date = resolveDeliveryDate(this.piInfo);
    return date ? formatDate(date) : "—";
  }

  private get deliveryDays() {
    const deliveryDays = this.displayValue(this.piInfo.deliveryDays);
    if (deliveryDays === "" || deliveryDays == null) {
      return "—";
    }
    return deliveryDays + "天";
  }

  private get remainingDaysText() {
    const value = calcRemainingDays(this.piInfo);
    if (value === "" || value == null || value === "-") {
      return "—";
    }
    if (value === "无") {
      return "无";
    }
    return `${value}天`;
  }

  private get overdueDaysText() {
    const value = calcOverdueDays(this.piInfo);
    if (value === "" || value == null || value === "-") {
      return "—";
    }
    return `${value}天`;
  }

  private get remainingClass() {
    const value = calcRemainingDays(this.piInfo);
    if (value === "无") {
      return "";
    }
    if (isRemainingWarning(value)) {
      return "is-warn";
    }
    if (typeof value === "number" && value > 10) {
      return "is-normal";
    }
    return "";
  }

  private get overdueClass() {
    const value = calcOverdueDays(this.piInfo);
    return typeof value === "number" && value > 0 ? "is-danger" : "";
  }

  private get deadlineTone() {
    if (this.overdueClass) return "is-overdue";
    if (this.remainingClass === "is-warn") return "is-warn";
    if (this.remainingClass === "is-normal") return "is-normal";
    if (isCompletedStatus(this.piInfo.shipmentStatus) && !this.overdueClass) {
      return "is-normal";
    }
    return "";
  }

  private get dialogDeliveryDate() {
    const firstOrder = parseDay(this.detailDeliveryDate.firstOrderDate);
    const days = Number(this.detailDeliveryDate.deliveryDays);
    if (!firstOrder || !Number.isFinite(days)) {
      return "";
    }
    return firstOrder.clone().add(days, "day").format("YYYY-MM-DD");
  }

  private get dialogDeliveryDateText() {
    if (!this.detailDeliveryDate.firstOrderDate) {
      return "--";
    }
    return this.dialogDeliveryDate || "--";
  }

  private get latestPoDeliveryDate() {
    const dates = (this.detailDeliveryDate.pos || [])
        .map((po) => parseDay(po.deliveryDate))
        .filter((date): date is moment.Moment => !!date);
    if (!dates.length) {
      return null;
    }
    return dates.reduce((latest, date) => (date.isAfter(latest) ? date : latest));
  }

  private get isDialogDeliveryDateEarlierThanPo() {
    const deliveryDate = parseDay(this.dialogDeliveryDate);
    const latestPoDate = this.latestPoDeliveryDate;
    if (!deliveryDate || !latestPoDate) {
      return false;
    }
    return deliveryDate.isBefore(latestPoDate);
  }

  private get shipmentBadgeClass() {
    const status = this.piInfo.shipmentStatus;
    if (status === "NOT_SHIPPED") return "is-not-shipped";
    if (status === "PARTIALLY_SHIPPED") return "is-partial";
    if (status === "COMPLETED") return "is-completed";

    const label = this.piInfo.status;
    if (label === "未发货") {
      return "is-not-shipped";
    }
    if (label === "部分发货") {
      return "is-partial";
    }
    if (label === "已完成") {
      return "is-completed";
    }
    return "is-unknown";
  }

  private displayValue(value: any) {
    if (value === 0) return "0";
    if (value == null || value === "") return "—";
    return value;
  }

  private formatDateTime(value?: string) {
    return value ? formatDate(value) || value : "—";
  }

  private async editDeliveryDate() {
    this.detailDeliveryDate = await detailQueryDeliveryDate(this.piId) as any;
    this.editDeliveryDateDialog = true;
  }

  private async confirmEditDeliveryDate() {
    await updatePiDeliveryDate(this.piId, {deliveryDays: this.detailDeliveryDate.deliveryDays})
        .then(() => {
          this.$notify({
            title: '成功',
            message: '这是一条成功的提示消息',
            type: 'success'
          });
          this.$emit("updateDeliveryDaysSuccess");
          this.editDeliveryDateDialog = false;
        });
  }
}
